/**
 * Hardcoded boilerplate copy for the SEO subsite layout (cloned from the Duda
 * "jbanville" template). Agent-specific bits (first name, full name, location)
 * are interpolated from subsiteData via the helpers in ./formatters.
 *
 * Per product decision: FAQ, process steps and product blurbs are NOT stored in
 * the data model — they are shared boilerplate for every agent.
 */
import { getFirstName, getFullName, getLocation } from './formatters'

const fallbackName = subsiteData => getFullName(subsiteData) || 'a USA Benefits Group agent'

const fallbackFirstName = subsiteData => getFirstName(subsiteData) || 'Your agent'

/** SEO <h1> / <title>. Includes the agent's home base when available. */
const getHeadline = subsiteData => {
  const location = getLocation(subsiteData)
  return location
    ? `Nationally Licensed Health, Medicare & Life Insurance Agent Based in ${location}`
    : 'Nationally Licensed Health, Medicare & Life Insurance Agent'
}

const getHeroSubhead = () =>
  'Personalized health, Medicare, life insurance, and employee benefits guidance from a trusted advisor who puts your needs first — wherever you are.'

const getHeroHeadline = subsiteData => {
  const headline = `${subsiteData?.s_hero_headline || ''}`.trim()
  return headline ? headline : getHeadline(subsiteData)
}

const getHeroIntro = subsiteData => {
  const intro = `${subsiteData?.s_hero_intro || ''}`.trim()
  return intro ? intro : getHeroSubhead()
}

const getMetaDescription = subsiteData => {
  const location = getLocation(subsiteData)
  const based = location ? ` Nationally licensed agent based in ${location}.` : ''
  return `Helping individuals, families, and small businesses nationwide find health insurance, Medicare, life insurance, and employee benefits solutions.${based} Contact ${fallbackName(
    subsiteData
  )} today.`
}

const META_KEYWORDS =
  'health insurance agency, health insurance broker, Medicare insurance agency, Medicare insurance broker, life insurance agency, small business health insurance, independent insurance agency, compare health insurance plans, compare Medicare plans, licensed insurance agents nationwide'

const MISSION_STATEMENT =
  'USA Benefits Group is a nationwide marketing company dedicated to the principles of service, integrity, professionalism and diversity. Our affiliated independent agents provide an extensive range of insurance products and pledge to honor our commitment to the clients and carriers by holding themselves to the highest ethical standards. Everything that we do is designed to ensure that our customers are presented quality products with a Servant’s Heart and a goal of exceeding expectations.'

/** Default "Meet the agent" bio used when the agent has no custom s_about. */
const getDefaultBio = subsiteData =>
  `${fallbackName(
    subsiteData
  )} is part of USA Benefits Group, a nationwide network of independent health and life insurance professionals serving individuals, families, small-business owners and the self-employed since 1988. As a licensed, independent advisor, ${fallbackFirstName(
    subsiteData
  )} represents you — not a single carrier — and is committed to matching you with the most appropriate and affordable coverage for your needs, with honesty, integrity and valuable solutions.`

/** FAQ accordion content (SEO). Generic, agent-agnostic insurance Q&A. */
const getFaqs = subsiteData => {
  const first = fallbackFirstName(subsiteData)
  return [
    {
      question: 'How much does it cost to work with an agent?',
      answer:
        'Nothing. Our consultations are completely free, with no obligation. We are paid by the insurance carriers, so you get expert, personalized guidance at no cost to you.',
    },
    {
      question: 'What types of insurance can you help me with?',
      answer:
        'We help with health insurance (including Marketplace/ACA plans), Medicare (Advantage, Supplement/Medigap and prescription drug plans), life insurance, and small-business group health and employee benefits.',
    },
    {
      question: 'Do you only offer one insurance company’s plans?',
      answer:
        'No. As an independent agency we represent many carriers, so we can compare options across companies and recommend the coverage that best fits your needs and budget.',
    },
    {
      question: 'When can I enroll in or change my coverage?',
      answer:
        'It depends on the product. Many plans have annual enrollment windows, but qualifying life events (such as a move, marriage, new baby, or loss of coverage) can open a special enrollment period. Reach out and we’ll let you know your options.',
    },
    {
      question: 'How do I get started?',
      answer: `Just book a free consultation. ${first} will take the time to understand your needs, answer your questions, and explain your options clearly — with no pressure and no cost.`,
    },
  ]
}

/** "Your Path to Better Coverage" — 3 step process. */
const getProcessSteps = subsiteData => {
  const first = fallbackFirstName(subsiteData)
  return [
    {
      number: 1,
      title: 'Book Your Consultation',
      body: `${first} takes the time to understand your needs, answer your questions, and explain things clearly—at no cost to you.`,
    },
    {
      number: 2,
      title: 'Compare Your Options',
      body: 'We compare available plans and walk you through what each option covers, what it costs, and how it works—so there are no surprises.',
    },
    {
      number: 3,
      title: 'Choose Your Coverage',
      body: "Once you're comfortable, you choose the plan that fits your health needs, budget, and goals. We handle the details and guide you through next steps.",
    },
  ]
}

/**
 * Insurance product cards. `quoteType` maps to the existing /quote flow; a
 * couple route to /contact where no matching quote tab exists.
 *
 * NOTE: the product photos currently point at the Duda CDN (cloned from the
 * reference template). These should be re-hosted on our own storage; if a URL
 * breaks the card still renders (the image is hidden on error).
 */
const PRODUCT_IMG = 'https://lirp.cdn-website.com/0b54bccb/dms3rep/multi/opt'
const PRODUCTS = [
  {
    key: 'health',
    icon: 'medkit',
    image: `${PRODUCT_IMG}/Untitled+design+%2844%29-1280w.png`,
    title: 'Health Insurance',
    body: 'Need coverage for yourself or your family? We help you compare Marketplace health plans, understand subsidies, and enroll in coverage that works for you.',
    quoteType: 'health',
  },
  {
    key: 'medicare',
    icon: 'user-md',
    image: `${PRODUCT_IMG}/Untitled+design+%2841%29-1280w.png`,
    title: 'Medicare Insurance',
    body: 'Simple guidance for Medicare Advantage, Medicare Supplement (Medigap), and prescription drug plans. We help you understand your options and choose coverage that fits your healthcare needs and budget.',
    quoteType: 'senior',
  },
  {
    key: 'life',
    icon: 'heartbeat',
    image: `${PRODUCT_IMG}/Untitled+design+%2842%29-1280w.png`,
    title: 'Life Insurance',
    body: 'Protect the people who matter most. We help you compare life insurance options designed to provide financial security and peace of mind for your loved ones.',
    quoteType: 'life',
  },
  {
    key: 'business',
    icon: 'briefcase',
    image: `${PRODUCT_IMG}/Untitled+design+%2843%29-1280w.png`,
    title: 'Small Business',
    body: 'Coverage solutions for small business owners, including group health options and employee benefits designed to support your team and your business.',
    quoteType: null, // routes to /contact
  },
]

/**
 * "Book Your Appointment" target. Uses the agent's configured booking link
 * (s_book_appt_link, set on the Website Info page) when present — opened in a
 * new tab — otherwise falls back to the internal contact page.
 */
const getBookingLink = subsiteData => {
  const link = `${subsiteData?.s_book_appt_link || ''}`.trim()
  if (link) {
    const href = /^((http|https):)|(\/\/)/i.test(link) ? link : `//${link}`
    return { href, external: true }
  }
  return {
    href: subsiteData?.u_login ? `/${subsiteData.u_login}/contact` : '#',
    external: false,
  }
}

const getProducts = subsiteData => {
  const login = subsiteData?.u_login
  // Subsite quoting is disabled — all product CTAs route to the contact page.
  return PRODUCTS.map(p => ({
    ...p,
    link: login ? `/${login}/contact` : '#',
  }))
}

const WHY_CHOOSE_STATS = [
  { stat: '38 Years', label: 'of experience protecting our customers' },
  { stat: 'Free Consultations', label: 'with no cost or obligation' },
  { stat: 'Independent', label: 'we compare plans across many carriers' },
]

export {
  getHeadline,
  getHeroSubhead,
  getHeroHeadline,
  getHeroIntro,
  getMetaDescription,
  META_KEYWORDS,
  MISSION_STATEMENT,
  getDefaultBio,
  getFaqs,
  getProcessSteps,
  getProducts,
  getBookingLink,
  WHY_CHOOSE_STATS,
}
